import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { BookOpen, Eye, EyeOff } from 'lucide-react';

export default function RegisterPage() {
  const [form, setForm] = useState({ email: '', password: '', name: '', apellidos: '', pronombres: '', profesion: '', especialidad: '', pais: '', orcid: '' });
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const set = (key) => (e) => setForm(f => ({ ...f, [key]: e.target ? e.target.value : e }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password.length < 6) { setError('La contrasena debe tener al menos 6 caracteres'); return; }
    setLoading(true);
    try {
      const user = await register(form);
      if (user.roles?.includes('Admin') || user.roles?.includes('Editor')) navigate('/panel');
      else if (user.roles?.includes('Revisor')) navigate('/panel/mis-revisiones');
      else navigate('/panel/mis-manuscritos');
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al registrarse');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-16">
      <div className="w-full max-w-lg animate-fade-up">
        <div className="text-center mb-8">
          <div className="w-10 h-10 rounded-sm bg-slate-dark flex items-center justify-center mx-auto mb-4">
            <BookOpen className="w-5 h-5 text-white" strokeWidth={1.5} />
          </div>
          <h1 className="text-2xl font-serif font-bold text-slate-dark">Crear Cuenta</h1>
          <p className="text-sm font-sans text-gray-400 mt-1">Unete a la comunidad de Archivos Trans</p>
          <p className="text-xs font-sans text-trans-blue mt-1">6 meses de acceso gratuito al registrarte</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4" data-testid="register-form">
          {error && <div className="p-3 text-sm font-sans bg-red-50 text-red-600 rounded-md border border-red-100" data-testid="register-error">{error}</div>}
          
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="font-sans text-sm">Nombre *</Label>
              <Input value={form.name} onChange={set('name')} required placeholder="Nombre" data-testid="register-name" className="font-sans" />
            </div>
            <div className="space-y-1.5">
              <Label className="font-sans text-sm">Apellidos</Label>
              <Input value={form.apellidos} onChange={set('apellidos')} placeholder="Apellidos" data-testid="register-apellidos" className="font-sans" />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="font-sans text-sm">Email *</Label>
            <Input type="email" value={form.email} onChange={set('email')} required placeholder="tu@email.com" data-testid="register-email" className="font-sans" />
          </div>

          <div className="space-y-1.5">
            <Label className="font-sans text-sm">Contrasena *</Label>
            <div className="relative">
              <Input type={showPw ? 'text' : 'password'} value={form.password} onChange={set('password')} required placeholder="Minimo 6 caracteres" data-testid="register-password" className="font-sans pr-10" />
              <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="font-sans text-sm">Pronombres</Label>
              <Input value={form.pronombres} onChange={set('pronombres')} placeholder="elle/el/ella" data-testid="register-pronombres" className="font-sans" />
            </div>
            <div className="space-y-1.5">
              <Label className="font-sans text-sm">Profesion</Label>
              <Input value={form.profesion} onChange={set('profesion')} placeholder="Ej: Medique" data-testid="register-profesion" className="font-sans" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="font-sans text-sm">Especialidad</Label>
              <Input value={form.especialidad} onChange={set('especialidad')} placeholder="Ej: Endocrinologia" data-testid="register-especialidad" className="font-sans" />
            </div>
            <div className="space-y-1.5">
              <Label className="font-sans text-sm">Pais</Label>
              <Input value={form.pais} onChange={set('pais')} placeholder="Ej: Mexico" data-testid="register-pais" className="font-sans" />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="font-sans text-sm">ORCID (opcional)</Label>
            <Input value={form.orcid} onChange={set('orcid')} placeholder="0000-0000-0000-0000" data-testid="register-orcid" className="font-sans" />
          </div>

          <Button type="submit" className="w-full bg-slate-dark hover:bg-slate-dark/90 font-sans" disabled={loading} data-testid="register-submit-btn">
            {loading ? 'Creando cuenta...' : 'Crear Cuenta'}
          </Button>
        </form>

        <p className="text-center text-sm font-sans text-gray-400 mt-6">
          Ya tienes cuenta?{' '}
          <Link to="/login" className="text-trans-blue hover:underline font-medium" data-testid="register-login-link">Inicia sesion</Link>
        </p>
      </div>
    </div>
  );
}
